import { Currency } from '@/components/form/fields/text/helpers/currency';
import { BulkBasicBetterBest } from '@/pages/product/product/bulk/helpers/types';
import { BulkFinanceModelTypes } from '@/pages/settings/bulk/helpers/utils';
import { Product } from '@/services/api/contracts/catalog/types';
import { z } from 'zod';

export const IncomingProductSpec = z.object({
  type: z.string().optional(),
  value: z.string()
});

export type IncomingProductSpec = z.infer<typeof IncomingProductSpec>;

export const IncomingProduct = z.object({
  Name: z.string().nonempty(),
  ManufacturerSKU: z.string().nonempty(),
  Manufacturer: z.string().nonempty(),
  Category: z.string().nonempty(),
  Description: z.string().optional(),
  Price: Currency,
  Margin: z.coerce.number(),
  Replacements: z.string().optional(),
  Specs: z.array(IncomingProductSpec),
  // Zod doesnt handle optional enums correctly.
  // Workaround from https://github.com/react-hook-form/react-hook-form/issues/3985
  BasicBetterBest: z.nativeEnum(BulkBasicBetterBest).or(z.literal('')).optional(),
  Region: z.string()
});

export type IncomingProduct = z.infer<typeof IncomingProduct>;

const parseSpecifications = (record: Record<string, string>) => {
  const Specs = Object.entries(record)
    .filter(([key]) => key.startsWith('Spec'))
    .filter(([, value]) => !!value)
    .map(([key, value]) => {
      const [, type] = key.split(':');
      return { type, value };
    });

  return { ...record, Specs };
};

export const IncomingProductPipeline = IncomingProduct.omit({ Specs: true })
  .catchall(z.string())
  .transform(parseSpecifications)
  .pipe(IncomingProduct);

export const IncomingCustomer = z.object({
  RegName: z.string().nonempty(),
  RegAddress1: z.string().nonempty(),
  RegAddress2: z.string(),
  RegAddressCity: z.string().nonempty(),
  RegAddressCounty: z.string().nonempty(),
  RegPostCode: z.string().nonempty(),
  RegCountry: z.string().nonempty(),
  RegNumber: z.string().nonempty(),
  NumEmployees: z.string().nonempty(),
  RegVatNumber: z.string().nonempty()
});

export type IncomingCustomer = z.infer<typeof IncomingCustomer>;

const parseEmployeeCount = (record: Record<string, string>) => {
  const employeeCounts = Object.entries(record)
    .filter(([key]) => key.startsWith('NumEmployees'))
    .filter(([, value]) => !!value)
    .map(([, value]) => parseInt(value, 10));

  return { ...record, employeeCounts };
};

export const IncomingCustomerPipeline = IncomingCustomer.catchall(z.string())
  .transform(parseEmployeeCount)
  .pipe(IncomingCustomer);

export const Replacement = z.object({
  Product: Product,
  Replaces: z.array(Product)
});

export type Replacement = z.infer<typeof Replacement>;

export const IncomingFinanceModel = z.object({
  Manufacturer: z.string(),
  Category: z.string().nonempty(),
  Type: z.nativeEnum(BulkFinanceModelTypes),
  Term: z.string(),
  Pcms: z.array(
    z.object({
      type: z.string(),
      value: z.coerce.number().default(0)
    })
  )
});

export type IncomingFinanceModel = z.infer<typeof IncomingFinanceModel>;

const parsePcms = (record: Record<string, string>) => {
  const Pcms = Object.entries(record)
    .filter(([key]) => key.startsWith('PCM'))
    .map(([key, value]) => {
      const [, type] = key.split(':');
      return { type, value: Number(value as string) };
    });

  return { ...record, Pcms };
};

export const IncomingFinanceModelPipeline = IncomingFinanceModel.omit({ Pcms: true })
  .catchall(z.string())
  .transform(parsePcms)
  .pipe(IncomingFinanceModel);
